import './styles/fonts.css';
import React, { useState, useEffect } from 'react';
import { HomePage } from './pages/HomePage';
import { ChatsListPage } from './pages/ChatsListPage';
import { ChatPage } from './pages/ChatPage';
import { ChatsList } from './components/chats/ChatsList';
import { ChatWindow } from './components/chat/ChatWindow';
import { LoadingScreen } from './components/ui/LoadingScreen';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useSocket } from './hooks/useSocket';
import { useSocketAuth } from './hooks/useSocket';
import { chatAPI, groupChatAPI } from './services/api';
import { Chat } from './types/chat';
import { ConfirmModal, InputModal } from './components/ui/Modal';
import './App.css';
import { ChatsProvider, useChatsContext } from './context/ChatsContext';
import {
  ExitIcon,
  QuestionIcon,
  RandomIcon,
  SearchIcon,
  BroomIcon,
  Trash2Icon,
  GroupIcon,
  QRCodeIcon,
} from './assets/SvgIcons';
import { WelcomeModal } from './components/ui/WelcomeModal';
import { storage } from './utils/storage';
import { GroupInfoModal } from './components/ui/GroupInfoModal';

type AppScreen = 'chats' | 'chat';

interface CreateGroupResponse {
  groupChat: {
    id: number;
    name: string | null;
    description: string | null;
    creatorId: number;
    inviteCode: string;
    groupCode: string;
    createdAt: string;
    isActive: boolean;
    maxMembers: number;
  };
  qrCodeData: string;
}

const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { chats, deleteChat, clearChat, createChatByNumber, joinGroupChat } = useChatsContext();
  const socket = useSocket();
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const [registrationData, setRegistrationData] = useState<any>(null);
  const { loadChats } = useChatsContext();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('chats');
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [isDesktop, setIsDesktop] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showFindByNumber, setShowFindByNumber] = useState(false);
  const [isRandomSearching, setIsRandomSearching] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingText, setLoadingText] = useState('Загрузка...');
  const [showGroupInfoModal, setShowGroupInfoModal] = useState(false);
  const [showShareGroupModal, setShowShareGroupModal] = useState(false);
  const [shareGroupData, setShareGroupData] = useState<{
    groupNumber: string;
    inviteCode: string;
    qrCodeData: string;
  } | null>(null);
  const [newGroupData, setNewGroupData] = useState<{
    chatId: number;
    groupCode: string;
    inviteCode: string;
    qrCodeData: string;
  } | null>(null);

  useSocketAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const inviteCode = urlParams.get('inviteCode');
    if (isAuthenticated && user && inviteCode) {
      handleJoinGroupByInvite(inviteCode);
    }
  }, [isAuthenticated, user]);

  const handleJoinGroupByInvite = async (inviteCode: string) => {
    try {
      setLoadingText('Присоединение к группе...');
      setShowLoadingScreen(true);

      const joinedChat = await joinGroupChat(inviteCode);

      const newUrl = window.location.pathname;
      window.history.replaceState(null, '', newUrl);

      await loadChats();

      let targetChat: Chat | undefined;
      if (joinedChat) {
        targetChat = {
          id: joinedChat.id,
          companionNumber: joinedChat.groupCode,
          companionId: joinedChat.creatorId || joinedChat.id,
          unreadCount: joinedChat.unreadCount,
          isNew: joinedChat.isNew || false,
          createdAt: joinedChat.createdAt,
          messages: joinedChat.messages,
          isGroupChat: true,
          groupCode: joinedChat.groupCode,
          lastMessage: joinedChat.lastMessage
            ? {
                preview: joinedChat.lastMessage.content.slice(0, 50),
                time: joinedChat.lastMessage.timestamp,
                senderNumber: joinedChat.lastMessage.senderNumber,
                isOwn: joinedChat.lastMessage.senderId === user?.id,
              }
            : undefined,
        };
      } else {
        targetChat = chats
          .filter((chat) => chat.isGroupChat)
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      }

      if (targetChat) {
        setSelectedChat(targetChat);
        if (!isDesktop) {
          setCurrentScreen('chat');
        }
      }

      setTimeout(() => {
        setShowLoadingScreen(false);
      }, 2000);
    } catch (error: any) {
      setShowLoadingScreen(false);
      const errorMessage = error.response?.data?.message || 'Ошибка при присоединении к группе';
      alert(errorMessage);
    }
  };

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (!isDesktop && currentScreen === 'chat' && selectedChat) {
        event.preventDefault();
        handleBackToChats();
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (!isDesktop && currentScreen === 'chat' && selectedChat) {
      window.history.pushState(null, '', window.location.href);
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDesktop, currentScreen, selectedChat]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const regData = storage.getRegistrationData();
      if (regData && !localStorage.getItem('hasSeenWelcome')) {
        setRegistrationData(regData);
        setShowWelcomeModal(true);
      }
    }
  }, [isAuthenticated, user]);

  const handleWelcomeClose = () => {
    localStorage.setItem('hasSeenWelcome', 'true');
    storage.clearRegistrationData();
    setShowWelcomeModal(false);
  };

  const handleLogoutConfirm = () => {
    logout();
    setShowLogoutConfirm(false);
    setShowMenu(false);
  };

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let inviteCode = urlParams.get('inviteCode') || urlParams.get('invite');

    if (!inviteCode && window.location.protocol !== 'http:' && window.location.protocol !== 'https:') {
      const hash = window.location.hash;
      const match = hash.match(/invite=([a-f0-9]+)/);
      if (match) {
        inviteCode = match[1];
      }
    }

    if (isAuthenticated && user && inviteCode) {
      handleJoinGroupByInvite(inviteCode);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const pendingInviteCode = sessionStorage.getItem('pendingInviteCode');
      if (pendingInviteCode) {
        handleJoinGroupByInvite(pendingInviteCode);
        sessionStorage.removeItem('pendingInviteCode');
      }
    }
  }, [isAuthenticated, user]);

  const handleChatSelect = (chat: Chat) => {
    console.log('handleChatSelect called with:', chat);
    setSelectedChat(chat);
    if (!isDesktop) {
      setCurrentScreen('chat');
    }
  };

  const handleBackToChats = () => {
    setCurrentScreen('chats');
    setSelectedChat(null);
  };

  const handleRandomSearch = async () => {
    if (isRandomSearching) return;

    try {
      setIsRandomSearching(true);
      setLoadingText('Поиск случайного собеседника...');
      setShowLoadingScreen(true);

      const response = await chatAPI.findRandomUser();
      createChatByNumber(response.data.userNumber);

      setTimeout(() => {
        setShowLoadingScreen(false);
        setIsRandomSearching(false);
        loadChats();
      }, 3000);
    } catch (error: any) {
      setShowLoadingScreen(false);
      setIsRandomSearching(false);
      alert(error.response?.data?.message || 'Ошибка поиска случайного пользователя');
    }
  };

  const handleFindByNumber = async (numberToFind: string) => {
    if (numberToFind.trim()) {
      try {
        setLoadingText('Поиск пользователя...');
        setShowLoadingScreen(true);

        createChatByNumber(numberToFind.trim());

        setTimeout(() => {
          setShowFindByNumber(false);
          setShowLoadingScreen(false);
          loadChats();
        }, 3000);
      } catch (error: any) {
        setShowLoadingScreen(false);
        alert(error.response?.data?.message || 'Ошибка поиска пользователя');
      }
    }
  };

  const handleShareGroup = async () => {
    if (!selectedChat || !selectedChat.isGroupChat) return;

    try {
      const response = await groupChatAPI.getInviteCode(selectedChat.id);
      const inviteCode = response.data.inviteCode;

      const currentOrigin = window.location.origin;
      const inviteLink = `${currentOrigin}/group?invite=${inviteCode}`;

      setShareGroupData({
        groupNumber: selectedChat.companionNumber,
        inviteCode: inviteCode,
        qrCodeData: inviteLink,
      });

      setShowShareGroupModal(true);
    } catch (error: any) {
      console.error('Ошибка загрузки данных группы:', error);
      alert(error.response?.data?.message || 'Ошибка загрузки данных группы');
    }
  };

  const handleCreateGroup = async () => {
    try {
      const response = await groupChatAPI.createGroup('', 100);
      const groupData = response.data as unknown as CreateGroupResponse;

      if (!groupData) throw new Error('Ответ сервера некорректный');

      setNewGroupData({
        chatId: groupData.groupChat.id,
        groupCode: groupData.groupChat.groupCode,
        inviteCode: groupData.groupChat.inviteCode,
        qrCodeData: groupData.qrCodeData,
      });

      setShowGroupInfoModal(true);
    } catch (error: any) {
      console.error('Ошибка создания группы:', error);
      alert(error.response?.data?.message || 'Ошибка создания группы');
    }
  };

  const handleDeleteChat = async () => {
    if (selectedChat) {
      await deleteChat(selectedChat.id);
      setSelectedChat(null);
      setShowDeleteConfirm(false);
    }
  };

  const handleClearChat = async () => {
    if (selectedChat) {
      await clearChat(selectedChat.id);
      setShowClearConfirm(false);
    }
  };

  const handleLoadingComplete = () => {
    setShowLoadingScreen(false);
  };

  const menuItems = [
    {
      id: 'what-is-this',
      label: 'ЧТО ЭТО?',
      icon: QuestionIcon,
      action: () => {
        alert('Это приложение для чата с анонимными пользователями!');
        setShowMenu(false);
      },
    },
    {
      id: 'logout',
      label: 'ВЫЙТИ',
      icon: ExitIcon,
      action: () => {
        setShowLogoutConfirm(true);
        setShowMenu(false);
      },
      hasBorder: true,
    },
  ];

  const menuItemsForGroup = [
    {
      id: 'what-is-this',
      label: 'ЧТО ЭТО?',
      icon: QuestionIcon,
      action: () => {
        alert('Это приложение для чата с анонимными пользователями!');
        setShowMenu(false);
      },
    },
    {
      id: 'share-group',
      label: 'ПОДЕЛИТЬСЯ',
      icon: QRCodeIcon,
      action: handleShareGroup,
    },
    {
      id: 'logout',
      label: 'ВЫЙТИ',
      icon: ExitIcon,
      action: () => {
        setShowLogoutConfirm(true);
        setShowMenu(false);
      },
      hasBorder: true,
    },
  ];

  const desktopActionButtons = [
    {
      id: 'random-chat',
      label: 'СЛУЧАЙНЫЙ ЧАТ',
      icon: RandomIcon,
      action: handleRandomSearch,
      isDisabled: isRandomSearching,
    },
    {
      id: 'find-by-number',
      label: 'НАЙТИ ПО НОМЕРУ',
      icon: SearchIcon,
      action: () => setShowFindByNumber(true),
      isDisabled: false,
    },
    {
      id: 'create-group',
      label: 'СОЗДАТЬ КОМНАТУ',
      icon: GroupIcon,
      action: handleCreateGroup,
      isDisabled: false,
    },
    {
      id: 'clear-chat',
      label: 'ОЧИСТИТЬ',
      icon: BroomIcon,
      action: () => setShowClearConfirm(true),
      isDisabled: !selectedChat || selectedChat.id === 0 || selectedChat.isGroupChat,
    },
    {
      id: 'delete-chat',
      label: 'УДАЛИТЬ',
      icon: Trash2Icon,
      action: () => setShowDeleteConfirm(true),
      isDisabled: !selectedChat || selectedChat.id === 0 || selectedChat.isGroupChat,
    },
  ];

  if (isLoading) {
    return <LoadingScreen isVisible={true} loadingText='Загрузка...' />;
  }

  if (!isAuthenticated) {
    return <HomePage />;
  }

  if (isDesktop) {
    return (
      <>
        {showWelcomeModal && user && (
          <WelcomeModal
            userNumber={registrationData.user.userNumber}
            loginCode={registrationData.user.loginCode}
            recoveryKeys={registrationData.user.recoveryKeys}
            onClose={handleWelcomeClose}
          />
        )}

        <div
          style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              backgroundColor: '#000',
              color: '#fff',
              padding: '4px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              height: '30px',
              borderBottom: '2px solid #e8e8e8',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div
                style={{
                  backgroundColor: '#000',
                  color: '#FFF',
                  fontFamily: 'Tiny5, monospace',
                  fontSize: '19px',
                  height: '26px',
                  width: '26px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRight: '2px solid #e8e8e8',
                }}
              >
                Ё
              </div>
              <div style={{ fontFamily: 'monospace', fontSize: '14px' }}>Мои чаты</div>
            </div>

            <button
              onClick={() => setShowMenu(!showMenu)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: '14px',
              }}
            >
              МЕНЮ ▼
            </button>
          </div>

          {showMenu && (
            <div
              style={{
                position: 'absolute',
                right: '16px',
                top: '30px',
                backgroundColor: '#fff',
                border: '2px solid #000',
                zIndex: 50,
                width: '192px',
              }}
            >
              <div
                style={{
                  padding: '8px 16px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                }}
              >
                № {user?.userNumber || 'Загрузка...'}
              </div>
              {selectedChat?.isGroupChat
                ? menuItemsForGroup.map((item) => (
                  <button
                    key={item.id}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      ...(item.hasBorder && { borderTop: '1px solid #000' }),
                    }}
                    onClick={item.action}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {React.createElement(item.icon)}
                    {item.label}
                  </button>
                ))
                : menuItems.map((item) => (
                  <button
                    key={item.id}
                    style={{
                      width: '100%',
                      padding: '8px 16px',
                      background: 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      ...(item.hasBorder && { borderTop: '1px solid #000' }),
                    }}
                    onClick={item.action}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f0f0f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {React.createElement(item.icon)}
                    {item.label}
                  </button>
                ))}
            </div>
          )}

          <div
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              zIndex: 10,
              padding: '16px 8px',
            }}
          >
            {desktopActionButtons.map((button) => (
              <div
                key={button.id}
                onClick={button.isDisabled ? undefined : button.action}
                style={{
                  padding: '8px 12px',
                  cursor: button.isDisabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'Tiny5, monospace',
                  fontSize: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  minWidth: '80px',
                  textAlign: 'center',
                  opacity: button.isDisabled ? 0.5 : 1,
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{ opacity: button.isDisabled ? 0.5 : 1 }}>{React.createElement(button.icon)}</div>
                {button.label}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <div
              style={{
                width: '320px',
                borderRight: '2px solid #000',
                overflowY: 'auto',
                backgroundColor: '#fff',
              }}
            >
              <ChatsList onChatSelect={handleChatSelect} selectedChatId={selectedChat?.id} isDesktop={true} />
            </div>

            <div style={{ flex: 1, overflow: 'hidden' }}>
              {selectedChat ? (
                <ChatWindow chat={selectedChat} onBack={handleBackToChats} isDesktop={true} />
              ) : (
                <div
                  style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'monospace',
                    fontSize: '16px',
                    color: '#666',
                    backgroundColor: '#e8e8e8',
                  }}
                >
                  Выберите чат из списка
                </div>
              )}
            </div>
          </div>
        </div>

        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteChat}
          title='Удаление чата'
          message={`Удалить чат с ${selectedChat?.companionNumber || 'пользователем'}?`}
          confirmText='УДАЛИТЬ'
          confirmColor={'#DA3300'}
          cancelText='НЕТ'
        />

        <ConfirmModal
          isOpen={showClearConfirm}
          onClose={() => setShowClearConfirm(false)}
          onConfirm={handleClearChat}
          title='Очистка чата'
          message='Очистить чат от своих сообщений?'
          confirmText='ОЧИСТИТЬ'
          confirmColor={'#DA3300'}
          cancelText='НЕТ'
        />

        <InputModal
          isOpen={showFindByNumber}
          onClose={() => setShowFindByNumber(false)}
          onConfirm={handleFindByNumber}
          title='ВВЕДИТЕ НОМЕР ПОЛЬЗОВАТЕЛЯ'
          placeholder='00000000'
          maxLength={8}
          inputType='text'
          confirmText='НАЙТИ'
          cancelText='ОТМЕНА'
        />

        <ConfirmModal
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogoutConfirm}
          title='ПОДТВЕРЖДЕНИЕ ВЫХОДА'
          message='Вы уверены, что хотите выйти из аккаунта?'
          confirmText='ВЫЙТИ'
          confirmColor={'#DA3300'}
          cancelText='ОТМЕНА'
        />

        <GroupInfoModal
          isOpen={showGroupInfoModal}
          onClose={() => {
            setShowGroupInfoModal(false);
            setNewGroupData(null);
          }}
          onConfirm={async (enteredName?: string) => {
            if (!newGroupData) return;

            try {
              setShowGroupInfoModal(false);

              if (enteredName && enteredName.trim()) {
                await groupChatAPI.updateGroupName(newGroupData.chatId, enteredName.trim());
              }

              await loadChats();

              const createdGroup = chats.find((c) => c.id === newGroupData.chatId && c.isGroupChat);
              if (createdGroup) {
                setSelectedChat(createdGroup);
                if (!isDesktop) {
                  setCurrentScreen('chat');
                }
              }

              setNewGroupData(null);
            } catch (err: any) {
              alert(err.response?.data?.message || 'Ошибка при переходе в комнату');
            }
          }}
          groupNumber={newGroupData?.groupCode || ''}
          inviteCode={newGroupData?.inviteCode || ''}
          qrCodeData={newGroupData?.qrCodeData}
        />

        <GroupInfoModal
          showOnly
          isOpen={showShareGroupModal}
          onClose={() => {
            setShowShareGroupModal(false);
            setShareGroupData(null);
          }}
          onConfirm={() => {}}
          groupNumber={shareGroupData?.groupNumber || ''}
          inviteCode={shareGroupData?.inviteCode || ''}
          qrCodeData={shareGroupData?.qrCodeData || ''}
        />

        <LoadingScreen
          isVisible={showLoadingScreen}
          onComplete={handleLoadingComplete}
          loadingText={loadingText}
          minDuration={5000}
        />
      </>
    );
  }

  if (currentScreen === 'chat' && selectedChat) {
    return (
      <>
        <ChatPage chat={selectedChat} onBack={handleBackToChats} />
        <LoadingScreen
          isVisible={showLoadingScreen}
          onComplete={handleLoadingComplete}
          loadingText={loadingText}
          minDuration={5000}
        />
      </>
    );
  }

  return (
    <>
      {showWelcomeModal && user && (
        <WelcomeModal
          userNumber={registrationData.user.userNumber}
          loginCode={registrationData.user.loginCode}
          recoveryKeys={registrationData.user.recoveryKeys}
          onClose={handleWelcomeClose}
        />
      )}

      <ChatsListPage onChatSelect={handleChatSelect} />
      <LoadingScreen
        isVisible={showLoadingScreen}
        onComplete={handleLoadingComplete}
        loadingText={loadingText}
        minDuration={5000}
      />
    </>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <ChatsProvider>
        <AppContent />
      </ChatsProvider>
    </AuthProvider>
  );
};

export default App;
